import type { Component } from 'vue';
import {
    PhCards,
    PhCode,
    PhListBullets,
    PhSquaresFour,
    PhUsersThree,
    PhUsersFour,
    PhCrownSimple,
    PhHand,
    PhEye,
    PhLock,
    PhTrash,
    PhPlus,
    PhPencilSimple,
    PhSparkle,
    PhGear,
    PhArrowRight,
    PhBookOpen,
    PhLightning,
    PhCurrencyEur,
    PhQuestion,
    PhShield,
    PhCheckCircle,
    PhWarningCircle,
    PhClock,
    PhArrowSquareIn,
    PhChartLine,
    PhRocketLaunch,
    PhPlugsConnected,
    PhEnvelope,
    PhSignIn,
    PhUser,
    PhWifiHigh,
    PhWifiNone,
    PhWifiSlash,
} from '@phosphor-icons/vue';

// Domain concept → Phosphor icon. Both landing and app import from here so
// the same concept always uses the same icon (e.g. "session" is always PhCards).
export const CONCEPT_ICONS = {
    // Core domain
    session: PhCards,
    stories: PhListBullets,
    dashboard: PhSquaresFour,
    participants: PhUsersThree,
    team: PhUsersFour,
    organizer: PhCrownSimple,
    voter: PhHand,
    spectator: PhEye,
    privateSession: PhLock,
    waiting: PhClock,
    joinSession: PhArrowSquareIn,

    // Presence — colour never carries these alone, so each state owns a shape.
    // One signal-strength family reads as a scale: full bars, no bars, cut off.
    presenceOnline: PhWifiHigh,
    presenceAway: PhWifiNone,
    presenceOffline: PhWifiSlash,

    // Actions
    create: PhPlus,
    edit: PhPencilSimple,
    destroy: PhTrash,
    celebrate: PhSparkle,
    signIn: PhSignIn,

    // Account / settings
    profile: PhUser,
    settings: PhGear,
    email: PhEnvelope,
    integrations: PhPlugsConnected,
    api: PhCode,

    // Section eyebrows (mostly landing but reusable)
    overview: PhBookOpen,
    howItWorks: PhLightning,
    stats: PhChartLine,
    cta: PhRocketLaunch,
    pricing: PhCurrencyEur,
    faq: PhQuestion,
    security: PhShield,

    // Status / feedback
    arrow: PhArrowRight,
    success: PhCheckCircle,
    warning: PhWarningCircle,
} as const satisfies Record<string, Component>;

export type ConceptKey = keyof typeof CONCEPT_ICONS;

export type ConceptTone = 'primary' | 'success' | 'warning' | 'error' | 'neutral';

// Domain concept → daisyUI tone (for use with <IconBadge tone="…"> and other
// semantic-color-driven components). Keep in sync with CONCEPT_ICONS.
export const CONCEPT_TONES = {
    organizer: 'warning',
    team: 'primary',
    voter: 'primary',
    spectator: 'neutral',
    privateSession: 'warning',
    presenceOnline: 'success',
    presenceAway: 'warning',
    presenceOffline: 'neutral',
    api: 'primary',
    destroy: 'error',
    celebrate: 'success',
    success: 'success',
    warning: 'warning',
} as const satisfies Partial<Record<ConceptKey, ConceptTone>>;
